import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { useAuditStatus, useAuditReport, useAuditVersions } from '@/hooks/useAmazonAudit'
import { useLatestRemake } from '@/hooks/useRemake'
import { markAuditViewed } from '@/lib/api/amazon-listing'
import { useScrollSpy } from '@/hooks/useScrollSpy'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/v2/ui/Tabs'
import { AuditProgressScreen } from './AuditProgressScreen'
import { AuditFailedScreen } from './AuditFailedScreen'
import { ProductDetailHeader } from './ProductDetailHeader'
import { ScoreBreakdown } from './ScoreBreakdown'
import { AuditSidebar, SECTIONS } from './AuditSidebar'
import { AuditVersionSelector } from './AuditVersionSelector'
import { ReportSections } from './ReportSections'
import { RemakeTabContent } from './RemakeTabContent'
import { MarketAnalysisHeader } from './MarketAnalysisHeader'
import { PotentialCheckView } from './PotentialCheckView'
import { CampaignCreatorModal } from './CampaignCreator/CampaignCreatorModal'
import { RemakeAddToProductBridge } from './RemakeAddToProductBridge'

export function AmazonListingPage() {
  const { t } = useTranslation()
  const { productId } = useParams<{ productId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  const auditIdParam = searchParams.get('audit_id')
  const remakeIdParam = searchParams.get('remake_id') ?? undefined
  const activeTab = searchParams.get('tab') ?? 'audit'
  const activeView = searchParams.get('view') ?? 'check'

  const { data: versions } = useAuditVersions(productId ?? null)

  // Latest audit job ID (always newest, used for remake tab)
  const latestAuditJobId = versions?.[0]?.id ?? null

  // Derive the active audit job ID: from query param or newest version
  const auditJobId = useMemo(() => {
    if (!versions || versions.length === 0) return null
    if (auditIdParam) {
      const found = versions.find(v => v.id === auditIdParam)
      return found ? auditIdParam : versions[0].id
    }
    return versions[0].id
  }, [versions, auditIdParam])

  const { data: status, isLoading: statusLoading } = useAuditStatus(auditJobId)
  const { data: report } = useAuditReport(
    status?.status === 'completed' || status?.status === 'partial' ? auditJobId : null
  )
  const { data: latestRemake } = useLatestRemake(auditJobId ?? undefined)

  const previousScores = useMemo(() => {
    if (!versions || !status?.version_number) return undefined
    const prev = versions.find(v => v.version_number === status.version_number! - 1)
    return prev?.scores
  }, [versions, status])

  const currentVersionNumber = useMemo(() => {
    if (!versions || !auditJobId) return undefined
    return versions.find(v => v.id === auditJobId)?.version_number
  }, [versions, auditJobId])

  // Mark audit as viewed when report loads (removes "Neu" badge)
  useEffect(() => {
    if (auditJobId && report) {
      markAuditViewed(auditJobId).catch(() => {})
    }
  }, [auditJobId, report])

  const sectionIds = useMemo(() => SECTIONS.map(s => s.id), [])
  const activeSection = useScrollSpy(sectionIds)
  const isNewProduct = report?.audit_mode === 'new_product'
  const [creatorOpen, setCreatorOpen] = useState(false)

  const applyIdentifiers = useMemo(
    () => ({
      sku: report?.source_input?.sku ?? null,
      gtin: report?.source_input?.gtin ?? null,
      asin: status?.asin ?? null,
    }),
    [report?.source_input?.sku, report?.source_input?.gtin, status?.asin]
  )

  const canApply =
    !!latestRemake?.text_result &&
    !!(applyIdentifiers.sku || applyIdentifiers.gtin || applyIdentifiers.asin)

  const handleUpgrade = () => {
    setCreatorOpen(true)
  }

  const handleTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', value)
    setSearchParams(next, { replace: true })
  }

  const handleViewChange = (value: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('view', value)
    setSearchParams(next, { replace: true })
  }

  if (statusLoading || !status) {
    return <div className="flex items-center justify-center h-64 text-neutral-500">{t('common.loading')}</div>
  }

  if (status.status === 'failed') {
    return <AuditFailedScreen job={status} />
  }

  if (status.status !== 'completed' && status.status !== 'partial') {
    return <AuditProgressScreen job={status} />
  }

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6">
      <ProductDetailHeader
        asin={status.asin ?? ''}
        tld={status.tld ?? ''}
        title={report?.product_title}
        mainImageUrl={report?.main_image_url}
        scores={isNewProduct || activeView === 'check' ? undefined : report?.scores}
        previousScores={isNewProduct || activeView === 'check' ? undefined : previousScores}
        jobId={auditJobId ?? undefined}
        productId={productId}
        startedAt={status.started_at}
        completedAt={status.completed_at}
        sourceType={status.source_type}
        analysisMode={status.analysis_mode}
        sourceInput={report?.source_input}
      />

      <div id="audit-report-content" className="space-y-6">
        {isNewProduct && <MarketAnalysisHeader />}

        <Tabs value={activeTab} onValueChange={handleTabChange} variant="pills">
          <div className="sticky top-0 z-30 -mx-3 px-3 md:-mx-6 md:px-6 pt-1 pb-2 bg-white dark:bg-neutral-950 border-b border-neutral-200 dark:border-neutral-800 shadow-sm flex items-center justify-between gap-3">
            <TabsList>
              <TabsTrigger value="audit">{t('amazonListing.tabs.auditReport')}</TabsTrigger>
              <TabsTrigger value="remake">{t('amazonListing.tabs.listingRemake')}</TabsTrigger>
            </TabsList>
            {canApply && (
              <RemakeAddToProductBridge
                remake={latestRemake ?? null}
                identifiers={applyIdentifiers}
                asin={status?.asin ?? ''}
                versionNumber={status?.version_number ?? 1}
                completedAt={status?.completed_at ?? ''}
                remakeId={latestRemake?.id ?? null}
              />
            )}
          </div>

          <TabsContent value="audit">
            <Tabs value={activeView} onValueChange={handleViewChange} variant="pills">
              <TabsList className="mb-4">
                <TabsTrigger value="check">{t('amazonListing.views.potentialCheck')}</TabsTrigger>
                <TabsTrigger value="full">{t('amazonListing.views.fullAudit')}</TabsTrigger>
              </TabsList>
            </Tabs>

            {activeView === 'check' && report ? (
              <PotentialCheckView report={report} onViewFull={() => handleViewChange('full')} onStartRemake={() => handleTabChange('remake')} />
            ) : (
              <>
                {!isNewProduct && report?.scores && <ScoreBreakdown scores={report.scores} />}
                <div className="md:hidden mb-4">
                  {versions && currentVersionNumber !== undefined && (
                    <AuditVersionSelector versions={versions} currentVersion={currentVersionNumber} productId={productId} />
                  )}
                </div>
                <div className="flex gap-6">
                  <div className="hidden md:block w-56 flex-shrink-0">
                    <AuditSidebar
                      activeSection={activeSection}
                      auditMode={report?.audit_mode}
                      versions={versions}
                      currentVersion={currentVersionNumber}
                      productId={productId}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    {report && <ReportSections report={report} onUpgrade={handleUpgrade} analysisMode={status?.analysis_mode} />}
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="remake">
            <div className="max-w-7xl mx-auto">
              <RemakeTabContent
                jobId={latestAuditJobId ?? auditJobId ?? ''}
                marketplace={report?.tld ?? 'de'}
                remakeId={remakeIdParam}
                productId={productId ?? ''}
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <CampaignCreatorModal
        open={creatorOpen}
        onClose={() => setCreatorOpen(false)}
        initialMode="standard"
      />

    </div>
  )
}
